/* eslint-disable @typescript-eslint/no-unused-vars */
import { mapDatacenterDetailToSections } from "kontrolEdge/constants/constants";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import DataCenterDetailsCard from "./DatacenterDetailsCard";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { useAppSelector } from "@redux/hooks";
import { useProcessingOverlay } from "@shared/hooks/useProcessingOverlay";
import { RootState } from "@redux/store/store";
import { fetchInternetConnectionAPI } from "kontrolEdge/components/Internet/requests/request";
import DatacenterBandwidthChart from "./DataCenterUtilizationChart/BandwidthChart";
import { fetchDatacenterDetailAPI, fetchDatacenterErrorsAPI, fetchDatacenterSubscriptionAPI } from "../requests/request";

// 1) The overall response shape
export interface DatacenterResponse {
  success: boolean;
  message: string;
  data: RawDatacenterDetail;
}

export interface Subnet {
  id: number;
  ip: string;
  mask: string;
  status: string;
}
export interface SubscriptionDetail {
  subscriptionId: string;
}
export interface RawDatacenterDetail {
  id: number;
  mbps: number;
  vlan: number;
  automaticVlan: boolean;
  redundancy: boolean;
  routing: string;
  terms: number;
  autoRenew: boolean;
  subscriptionId: string;
  port: {
	id: number;
	name: string;
	datacenter: {
	datacenterId: string;
	name: string;
	displayName: string;
	direction: string;
	gps: string;
	capacityKW: string;
	phase: string;
	cage: string;
	rackId: string;
	rackCapacity: string;
	patchPanelODF: string;
	rackSecurityPin: string;
	status: string;
	visible: boolean;
	providerCode: string;
	city: string;
	providerId: number;
	createdAt: string;
	updatedAt: string;
	};
  };
  lag: null;
  performanceMetrics?: {
	errors: number;
  };
  subscription?: SubscriptionDetail;
  subnets: Array<{
	id: number;
	ip: string;
	mask: string;
	status: string;
  }>;
}

type LocationState = { datacenterDetail?: RawDatacenterDetail, tenantOverride?: number; };
  
const DatacenterDetails: React.FC = () => {
	//Grab the raw Location object
	const location = useLocation();
	const reduxTenantId = useAppSelector((state) => state.selection.tenantId);
	//Assert its state shape
	const { datacenterDetail: preloaded, tenantOverride } =
		(location.state as LocationState) || {};
	const [searchParams] = useSearchParams();
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const navigate = useNavigate();

	//Port id selected from MainPort
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	const datacenterId = searchParams.get("datacenterId")!;
	//Seed from navigation‑state if available
	const [detail, setDetail] = useState<RawDatacenterDetail | undefined>(preloaded);
	const [isFetching, setIsFetching] = useState(false);
	const overlay = useProcessingOverlay(isFetching);
	const api = useAxiosAuth0();
	const { tenantId } = useAppSelector(state => state.selection);
	const userProfile = useAppSelector((state: RootState) => state.userProfile);
	const { canAccessAlerts } = userProfile;
	const effectiveTenantId = tenantOverride ?? reduxTenantId;

	const sections = useMemo(
		() => mapDatacenterDetailToSections({ data: detail }),
		[detail]
	);
		useEffect(() => {
		//Re-run whenever datacenterId or refresh changes
		if (!datacenterId) return;
		let cancelled = false;
		setIsFetching(true);
	
		(async () => {
		try {
			//Core detail – show it immediately fetchDatacenterDetailAPI
			const coreResp = await fetchDatacenterDetailAPI(api, datacenterId, effectiveTenantId);
			const fresh: RawDatacenterDetail = coreResp.data.data;
			if (cancelled) return;
			setDetail(fresh);
	
			//Extras in parallel
			const [
			errorsSettled,
			subscriptionSettled
			] = await Promise.allSettled([
			fetchDatacenterErrorsAPI(api, datacenterId, tenantId),
			fetchDatacenterSubscriptionAPI(api, datacenterId, tenantId),
			]);
	
			//Merge successful results
			if (errorsSettled.status === 'fulfilled') {
			fresh.performanceMetrics = {
				...fresh.performanceMetrics,
				errors: errorsSettled.value.data.data.crcErrors
			};
			} else {
			console.warn('CRC errors failed:', errorsSettled.reason);
			}

			if (subscriptionSettled.status === 'fulfilled') {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				(fresh as any).subscription = subscriptionSettled.value.data.data;
			} else {
			console.warn('Subscription fetch failed:', subscriptionSettled.reason);
			}
	
			if (cancelled) return;
			//Push the merged object back into state
			setDetail({ ...fresh });
	
		} catch (err) {
			console.error('Error loading InternetDetails:', err);
		} finally {
			if (!cancelled) setIsFetching(false);
		}
		})();
	
		return () => { cancelled = true; };
	}, [api, datacenterId]);
		
		if (isFetching || !detail) return <>{overlay}</>;
  

	return (
		<>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4 auto-rows-auto">
			{sections.map((section, idx) => (
				<DataCenterDetailsCard key={idx} data={section} />
			))}
			</div>
			{/* DataCenter Utilization chart */}
			<DatacenterBandwidthChart datacenterId={datacenterId} />
		</>
	);
};

export default DatacenterDetails;
